'use client';

import Image from 'next/image';
import dynamic from 'next/dynamic';
import { AnimationContainer } from '@/components/ui/animation-container';
import { Sparkles, ArrowRight, CheckCircle, FileText, Users, Zap, Settings } from 'lucide-react';
import type { TranslationKeys } from '../types';
import { copyOr, type SiteCopy } from '@/lib/marketing/site-copy';

const BorderBeam = dynamic(() => import('@/components/ui/border-beam').then(m => ({ default: m.BorderBeam })), { ssr: false });

const DEFAULT_DEMO_CTA_URL = 'https://meetings-na2.hubspot.com/roberto-rodriguez';

interface HeroSectionProps {
  t: TranslationKeys;
  copy?: SiteCopy;
  onOpenHowItWorksVideo?: () => void;
}

export default function HeroSection({ t, copy, onOpenHowItWorksVideo }: HeroSectionProps) {
  const title = copyOr(copy, 'home.hero.title', t.heroTitle);
  const titleHighlight = copyOr(copy, 'home.hero.title_highlight', t.heroTitleHighlight);
  const subtitle = copyOr(copy, 'home.hero.subtitle', t.heroDesc);
  const subtitle2 = copyOr(copy, 'home.hero.subtitle_secondary', t.heroDesc2);
  const ctaPrimary = copyOr(copy, 'home.hero.cta_primary', t.startTrial);
  const ctaSecondary = copyOr(copy, 'home.hero.cta_secondary', t.seeHow);
  const ctaDemo = copyOr(copy, 'home.hero.cta_demo', t.bookDemo);
  const ctaDemoHref = copyOr(copy, 'home.hero.cta_demo_url', DEFAULT_DEMO_CTA_URL);

  // Per-locale font size overrides from /admin/copy. Marketing controls
  // the desktop ceiling and the mobile floor independently; both optional.
  //
  //   • Empty desktop → fall back to the responsive Tailwind classes.
  //   • Empty mobile  → auto floor = 55% of the desktop value.
  //   • Both empty    → no inline style at all.
  //
  // The rendered h1 uses clamp(mobile, 6vw, desktop) to scale fluidly.
  const parsePx = (raw: string, min: number, max: number): number | null => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < min || n > max) return null;
    return n;
  };
  const titleSizePx = parsePx(copyOr(copy, 'home.hero.title.size_px', ''), 24, 128);
  const titleSizeMobilePx = parsePx(copyOr(copy, 'home.hero.title.size_px_mobile', ''), 18, 96);
  const titleClamp = (() => {
    if (!titleSizePx) return null;
    const mobile = titleSizeMobilePx ?? Math.round(titleSizePx * 0.55);
    return `clamp(${mobile}px, 6vw, ${titleSizePx}px)`;
  })();
  return (
      <>
      {/* Grid Pattern Background - Base */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(168, 85, 247, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(168, 85, 247, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Hero Section */}
      <section className="pt-28 pb-2 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col items-center justify-center w-full text-center">
              <AnimationContainer className="flex flex-col items-center justify-center w-full text-center">
                <a href="/sign-up" className="group relative z-20 grid overflow-hidden rounded-full px-4 py-1 shadow-[0_1000px_0_0_hsl(0_0%_85%)_inset] transition-colors duration-200 mb-6 no-underline touch-manipulation">
                  <span className="pointer-events-none">
                    <span className="spark mask-gradient absolute inset-0 h-[100%] w-[100%] animate-flip overflow-hidden rounded-full [mask:linear-gradient(white,_transparent_50%)] before:absolute before:aspect-square before:w-[200%] before:rotate-[-90deg] before:animate-rotate before:bg-[conic-gradient(from_0deg,transparent_0_340deg,#9333ea_360deg)] before:content-[''] before:[inset:0_auto_auto_50%] before:[translate:-50%_-15%]" />
                  </span>
                  <span className="pointer-events-none backdrop absolute inset-[1px] rounded-full bg-white transition-colors duration-200 group-hover:bg-gray-50" />
                  <span className="pointer-events-none h-full w-full blur-md absolute bottom-0 inset-x-0 bg-gradient-to-tr from-purple-500/10"></span>
                  <span className="z-10 py-0.5 text-sm text-gray-700 flex items-center justify-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    {t.badge}
                    <ArrowRight className="ml-1 w-3 h-3 transition-transform duration-300 ease-in-out group-hover:translate-x-0.5" />
                  </span>
                </a>

                <h1
                  className={`text-gray-900 text-center py-6 font-bold tracking-tight text-balance !leading-[1.1] w-full${titleClamp ? '' : ' text-4xl sm:text-5xl md:text-6xl lg:text-7xl'}`}
                  style={titleClamp ? { fontSize: titleClamp } : undefined}
                >
                  {title}{' '}
                  <span className="text-transparent bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text">
                    {titleHighlight}
                  </span>
                </h1>

                <p className="mb-10 text-lg tracking-tight text-gray-600 md:text-xl text-balance max-w-2xl">
                  {subtitle}
                  {subtitle2 && (
                    <>
                      <br className="hidden md:block" />
                      <span className="hidden md:block">{subtitle2}</span>
                    </>
                  )}
                </p>

                <div className="relative z-20 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                  <a
                    href="/sign-up"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 sm:px-8 h-12 sm:h-12 text-sm sm:text-base font-medium bg-gray-900 hover:bg-gray-800 text-white no-underline transition-colors touch-manipulation cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {ctaPrimary}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href={ctaDemoHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 sm:px-8 h-12 sm:h-12 text-sm sm:text-base font-medium bg-purple-600 hover:bg-purple-700 text-white no-underline transition-colors touch-manipulation cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {ctaDemo}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => onOpenHowItWorksVideo?.()}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 sm:px-8 h-12 sm:h-12 text-sm sm:text-base font-medium border border-[#ab5af6]/55 bg-white hover:bg-[#ab5af6]/5 text-gray-900 no-underline transition-colors touch-manipulation cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {ctaSecondary}
                  </button>
                </div>
              </AnimationContainer>

              {/* Hero Image with Border Beam */}
              <AnimationContainer
                delay={0.2}
                className="relative pt-12 pb-8 md:pt-16 md:pb-10 px-2 bg-transparent w-full max-w-5xl"
              >
                <div className="absolute md:top-[10%] left-1/2 gradient w-3/4 -translate-x-1/2 h-1/4 md:h-1/3 inset-0 blur-[5rem] animate-image-glow pointer-events-none"></div>
                <div className="relative -m-2 rounded-xl p-2 ring-1 ring-inset ring-gray-300 lg:-m-4 lg:rounded-2xl bg-white/80 backdrop-blur-sm">
                  <BorderBeam size={250} duration={12} delay={9} colorFrom="#a855f7" colorTo="#6366f1" borderWidth={2} />
                  {/* ORIGINAL HERO IMAGE (uncomment to revert):
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="flex-1 text-center">
                        <span className="text-xs text-gray-400">EZDocuAI - Document Workspace</span>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 divide-x divide-gray-100">
                      <div className="p-8 bg-gray-50/50">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">{t.demoOriginalDocument}</div>
                        <div className="space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-full"></div>
                          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                          <div className="h-4 bg-gray-200 rounded w-4/5 mt-6"></div>
                          <div className="h-4 bg-gray-200 rounded w-full"></div>
                        </div>
                      </div>
                      <div className="p-8">
                        <div className="text-xs font-medium text-purple-600 uppercase tracking-wider mb-4">Translation</div>
                        <div className="space-y-3">
                          <div className="h-4 bg-purple-100 rounded w-3/4"></div>
                          <div className="h-4 bg-purple-100 rounded w-full"></div>
                          <div className="h-4 bg-purple-100 rounded w-5/6"></div>
                          <div className="h-4 bg-purple-100 rounded w-2/3"></div>
                          <div className="h-4 bg-purple-100 rounded w-4/5 mt-6"></div>
                          <div className="h-4 bg-purple-100 rounded w-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  */}
                  {/* Realistic app mockup */}
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    {/* Browser chrome */}
                    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="flex-1 mx-12">
                        <div className="bg-white rounded-md border border-gray-200 px-3 py-1 text-center">
                          <span className="text-xs text-gray-500">www.ezdocu.ai/orders</span>
                        </div>
                      </div>
                    </div>
                    {/* App layout */}
                    <div className="flex" style={{ minHeight: '320px' }}>
                      {/* Sidebar */}
                      <div className="w-[180px] border-r border-gray-100 bg-white p-3 hidden md:flex flex-col">
                        <div className="flex items-center gap-2 mb-5">
                          <Image src="/images/brand/ezdocuai-logo.png" alt="EZDocuAI" width={24} height={24} className="h-6 w-6" priority />
                          <span className="text-sm font-bold text-gray-900">EZDocuAI</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 px-2.5 py-2 bg-purple-50 rounded-lg">
                            <FileText className="w-4 h-4 text-purple-600" />
                            <span className="text-xs font-medium text-purple-700">{t.demoNavOrders}</span>
                          </div>
                          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-gray-50">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="text-xs text-gray-500">{t.demoNavTeam}</span>
                          </div>
                          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-gray-50">
                            <Zap className="w-4 h-4 text-gray-500" />
                            <span className="text-xs text-gray-500">{t.demoNavActivity}</span>
                          </div>
                          <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-gray-50">
                            <Settings className="w-4 h-4 text-gray-500" />
                            <span className="text-xs text-gray-500">{t.demoNavSettings}</span>
                          </div>
                        </div>
                        <div className="mt-auto pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-2 px-2">
                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-[10px] font-bold text-purple-800">A</span>
                            </div>
                            <div>
                              <div className="text-[10px] font-medium text-gray-700">{t.demoAdminUser}</div>
                              <div className="text-[9px] text-gray-500">{t.demoProPlan}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Main content */}
                      <div className="flex-1 flex flex-col">
                        {/* Purple header with credits */}
                        <div className="bg-purple-600 px-4 py-2 flex items-center justify-between">
                          <span className="text-xs text-white">{t.demoHeaderDashboard}</span>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-1">
                              <Zap className="w-3 h-3 text-yellow-300" />
                              <span className="text-[10px] text-white font-medium">45 {t.demoCredits}</span>
                            </div>
                          </div>
                        </div>
                        {/* Stepper */}
                        <div className="bg-white border-b border-gray-100 px-2 sm:px-4 py-2.5 flex items-center justify-center gap-0.5 sm:gap-1">
                          {[
                            { label: t.demoStepUpload, done: true },
                            { label: t.demoStepProcessing, done: true },
                            { label: t.demoStepReview, active: true },
                            { label: t.demoStepTranslate, done: false },
                          ].map((step, i) => (
                            <div key={i} className="flex items-center gap-0.5 sm:gap-1">
                              <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center ${
                                step.done ? 'bg-green-500' : step.active ? 'bg-purple-600' : 'bg-gray-200'
                              }`}>
                                {step.done ? (
                                  <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                                ) : (
                                  <span className={`text-[7px] sm:text-[8px] font-bold ${step.active ? 'text-white' : 'text-gray-500'}`}>{i + 1}</span>
                                )}
                              </div>
                              <span className={`hidden sm:inline text-[10px] ${step.active ? 'text-purple-700 font-medium' : step.done ? 'text-gray-600' : 'text-gray-500'}`}>
                                {step.label}
                              </span>
                              {i < 3 && <div className={`w-4 sm:w-6 h-px mx-0.5 sm:mx-1 ${step.done ? 'bg-green-300' : 'bg-gray-200'}`} />}
                            </div>
                          ))}
                        </div>
                        {/* Split editor content */}
                        <div className="flex-1 grid md:grid-cols-2 divide-x divide-gray-100">
                          <div className="p-5 bg-gray-50/50">
                            <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-3">{t.demoOriginalDocument} (ES)</div>
                            <div className="space-y-2.5">
                              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-3 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/5 mt-4"></div>
                              <div className="h-3 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                            </div>
                          </div>
                          <div className="p-5">
                            <div className="text-[10px] font-medium text-purple-700 uppercase tracking-wider mb-3">{t.demoTranslation} (EN)</div>
                            <div className="space-y-2.5">
                              <div className="h-3 bg-purple-100 rounded w-3/4"></div>
                              <div className="h-3 bg-purple-100 rounded w-full"></div>
                              <div className="h-3 bg-purple-100 rounded w-5/6"></div>
                              <div className="h-3 bg-purple-100 rounded w-2/3"></div>
                              <div className="h-3 bg-purple-100 rounded w-4/5 mt-4"></div>
                              <div className="h-3 bg-purple-100 rounded w-full"></div>
                              <div className="h-3 bg-purple-100 rounded w-3/4"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 inset-x-0 w-full h-1/2 bg-gradient-to-t from-white z-40"></div>
                </div>
              </AnimationContainer>
            </div>
          </div>
      </section>
      </>
  );
}
